import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
    IconArrowRight,
    IconChevronSprite,
    IconChevronDown,
} from '$partials/icons/index.js';

import Image from '$partials/media/image.jsx';
import ModuleWalkthroughMobile from './walkthrough-mobile.jsx';
import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale.jsx';

gsap.registerPlugin(useGSAP);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleWalkthrough = ({
    eyebrow,
    title,
    itemsCollection,
    itemsVariant,
    mainCtaLabel,
    mainCtaLink,
    standfirst,
    parent
}) => {
    const { formatURI } = useLocale();

    const containerRef = useRef();

    const [activePanel, setActivePanel] = useState(0);
    const [windowSize, setWindowSize] = useState({ height: undefined, width: undefined });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth !== windowSize.width) {
                setWindowSize({ height: window.innerHeight, width: window.innerWidth });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [windowSize.width]);

    useGSAP(() => {
        const easing = 'power4.out';
        // Resizing panels
        gsap.to('.tab[data-selected] .tab-text', {
            height: 'auto',
            ease: easing,
            duration: 0.4
        });
        gsap.to('.tab:not([data-selected]) .tab-text', {
            height: 0,
            ease: easing,
            duration: 0.4
        });

        // Fading texts
        gsap.to('.tab[data-selected] .tab-text p', {
            opacity: 1,
            duration: 0.4
        });
        gsap.to('.tab:not([data-selected]) .tab-text p', {
            opacity: 0,
            duration: 0.2
        });

        // Cross-fading illustration
        gsap.to('.tab-panel:not([data-selected]) .tab-panel-illustration', {
            opacity: 0,
            duration: 0.3
        });

        gsap.to('.tab-panel[data-selected] .tab-panel-illustration', {
            opacity: 1,
            duration: 0.3,
        });
    }, {
        dependencies: [activePanel],
        scope: containerRef
    });

    return (
        <>
            <section className={`hidden md:block relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
                <div
                    className='layout-container'
                    ref={containerRef}>

                    <div className=''>
                        <div className='module-walk-through'>
                            {eyebrow && (
                                <h1
                                    className='type-style-11 text-core-brand px-10 sm:px-5 md:px-10'>
                                    {eyebrow}
                                </h1>
                            )}

                            <h2 className='type-style-2 text-grey-black mt-10 px-10 sm:px-5 md:px-10'>
                                {title}
                            </h2>

                            {standfirst && (
                                <p className='type-style-8 text-grey-off-black mt-12 px-10 sm:px-5 md:px-10 sm:w-10/12 md:w-8/12'>
                                    {standfirst}
                                </p>
                            )}

                            {itemsCollection?.items?.length > 0 && (
                                <TabGroup
                                    vertical
                                    onChange={(index) => {setActivePanel(index);}}>
                                    <div className='flex relative md:px-10 mt-50'>
                                        <div className="flex flex-col w-5/12 gap-16">
                                            <TabList className='flex flex-col gap-16'>
                                                {itemsCollection.items.map((itm, idx) => {
                                                    return (
                                                        <div
                                                            className='bg-grey mr-12 min-h-[84px] rounded-button'
                                                            key={idx}>
                                                            <Tab className='tab text-left outline-none group'>
                                                                <span className='type-style-4 font-[500] w-full p-[1.7rem] pb-5 pl-24 inline-flex gap-16 items-center'>
                                                                    <span
                                                                        className='text-dark-grey group-hover:text-core-brand group-data-[selected]:text-core-brand'>
                                                                        {itemsVariant === 'Numbered' ?
                                                                            String(idx + 1).padStart(2, '0') :
                                                                            <IconChevronDown className='group-data-[selected]:-rotate-90 transition-transform' />
                                                                        }
                                                                    </span>

                                                                    <span className='group-hover:text-black transition-colors duration-100 group-data-[selected]:text-black text-dark-grey'>
                                                                        {itm.heading}
                                                                    </span>
                                                                </span>

                                                                <div
                                                                    className='tab-text overflow-hidden'
                                                                    style={{height: idx === 0 ? 'auto' : 0}}>
                                                                    <div className='pl-24 pr-30 pb-30'>
                                                                        {itm?.copy && (
                                                                            <p className='text-grey-off-black'>
                                                                                {itm.copy}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </Tab>
                                                        </div>
                                                    );
                                                })}
                                            </TabList>

                                            {/* Button */}
                                            {mainCtaLabel && mainCtaLink && (
                                                <div>
                                                    <Button
                                                        as='a'
                                                        className='button button--secondary-alt'
                                                        href={formatURI(mainCtaLink)}
                                                        type='button'>
                                                        <span>{mainCtaLabel}</span>
                                                        <span className='button__icon button__icon--animate'>
                                                            <IconChevronSprite />
                                                        </span>
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        <TabPanels className='w-7/12'>
                                            <div className='relative mr-12 min-h-[84px] rounded-button'>
                                                {/* Spacer */}
                                                <div className='aspect-square' />

                                                {itemsCollection.items.map((itm, idx) => {
                                                    return (
                                                        <TabPanel
                                                            className='tab-panel absolute top-0 right-0 w-full px-10 sm:px-5 md:px-10'
                                                            key={idx}
                                                            static={true}
                                                            unmount={false}>
                                                            <div className='tab-panel-illustration bg-grey pointer-events-none select-none'>
                                                                {itm?.media?.contentType?.includes('video/') ?
                                                                    (
                                                                        <video
                                                                            autoPlay
                                                                            loop
                                                                            muted
                                                                            playsInline
                                                                            className='w-full h-full object-cover aspect-square rounded-button'
                                                                            src={itm?.media?.src}
                                                                            width={'100%'} />
                                                                    ) : (
                                                                        <Image
                                                                            {...itm?.media}
                                                                            className='h-full w-full object-center object-cover select-none pointer-events-none aspect-square rounded-button'
                                                                            containerClassName='w-full h-full relative'
                                                                            height='100%'
                                                                            loading=''
                                                                            loadWidth={998}
                                                                            width='100%' />
                                                                    )
                                                                }
                                                            </div>
                                                        </TabPanel>
                                                    );
                                                })}
                                            </div>
                                        </TabPanels>
                                    </div>


                                </TabGroup>
                            )}

                        </div>
                    </div>
                </div>
            </section>

            {(windowSize.width < 1024) && (
                <ModuleWalkthroughMobile
                    eyebrow={eyebrow}
                    itemsCollection={itemsCollection}
                    itemsVariant={itemsVariant}
                    mainCtaLabel={mainCtaLabel}
                    mainCtaLink={mainCtaLink}
                    parent={parent}
                    standfirst={standfirst}
                    title={title} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    itemsCollection: PropTypes.object,
    itemsVariant: PropTypes.string,
    mainCtaLabel: PropTypes.string,
    mainCtaLink: PropTypes.string,
    nextTypename: PropTypes.string,
    standfirst: PropTypes.string,
    parentTypename: PropTypes.string,
    parent: PropTypes.string,
};

ModuleWalkthrough.propTypes = propTypes;

export default ModuleWalkthrough;
